import {
  SortAscendingOutlined,
  SortDescendingOutlined
} from "@ant-design/icons"
import {
  Button,
  Dropdown,
  Flex,
  type MenuProps,
  Space,
  Tooltip,
  Typography
} from "antd"
import React, { useMemo, useCallback } from "react"

import type { ProjectListItem } from "../../types/types.ts"

/** What `ProjectViewSet.ordering_fields` accepts. Not every term is a
 *  rendered field: `customer_name` resolves server-side to the customer the
 *  card shows. */
export type ProjectOrderingTerm = keyof ProjectListItem | "customer_name"

export interface SortOption {
  /** `null` clears the ordering. */
  key: ProjectOrderingTerm | null
  /** The direction a first click on this term starts from; `asc` unless set. */
  firstOrder?: "asc" | "desc"
  hidden?: boolean
  icon?: React.ReactNode
  label: string
}

type SortOrder = "asc" | "desc" | null

function parseSortValue(value: string | null): {
  key: string | null
  order: SortOrder
} {
  if (!value) return { key: null, order: null }
  if (value.startsWith("-")) {
    return { key: value.slice(1), order: "desc" }
  }
  return { key: value, order: "asc" }
}

function buildSortValue(
  key: ProjectOrderingTerm | null,
  order: SortOrder
): string | null {
  if (!order || key == null) return null
  return order === "desc" ? `-${key}` : key
}

function getNextSortOrder(
  currentOrder: SortOrder,
  firstOrder: "asc" | "desc" = "asc"
): SortOrder {
  const secondOrder = firstOrder === "asc" ? "desc" : "asc"
  if (currentOrder === null) return firstOrder
  if (currentOrder === firstOrder) return secondOrder
  return null
}

interface ProjectListGridFiltersOrderingProps {
  ordering: string | null
  sortOptions: SortOption[]
  onOrderingChange: (data: string | null) => void
}

export function ProjectListGridFiltersOrdering({
  ordering,
  sortOptions,
  onOrderingChange
}: ProjectListGridFiltersOrderingProps) {
  const currentSort = useMemo(
    () => parseSortValue(ordering ?? null),
    [ordering]
  )

  const onSortClick = useCallback(
    (option: SortOption) => {
      const { key } = option
      const isSameKey = currentSort.key === key
      const nextOrder = isSameKey
        ? getNextSortOrder(currentSort.order, option.firstOrder)
        : (option.firstOrder ?? "asc")
      const newValue = buildSortValue(key, nextOrder)
      onOrderingChange(newValue)
    },
    [currentSort.key, currentSort.order, onOrderingChange]
  )

  const activeSortLabel = useMemo(() => {
    if (!currentSort.key) return null
    const option = sortOptions.find((o) => o.key === currentSort.key)
    return option?.label
  }, [currentSort.key, sortOptions])

  const sortMenuItems: MenuProps["items"] = useMemo(() => {
    return sortOptions.map((option) => {
      const isActive = currentSort.key === option.key
      const order = isActive ? currentSort.order : null

      return {
        ...option,
        // AntD keys a menu item by string; the clearing entry has no term.
        key: option.key ?? "clear-ordering",
        label: (
          <Space>
            <Flex justify="space-between" align="center" gap={12}>
              <Typography.Text strong={isActive}>
                {option.label}
              </Typography.Text>
              {isActive && order && (
                <Typography.Text strong className={"text-base"}>
                  {order === "asc" ? (
                    <SortAscendingOutlined />
                  ) : (
                    <SortDescendingOutlined />
                  )}
                </Typography.Text>
              )}
            </Flex>
          </Space>
        ),
        onClick: () => onSortClick(option)
      }
    })
  }, [sortOptions, currentSort, onSortClick])

  return (
    <Dropdown
      menu={{ items: sortMenuItems }}
      trigger={["click"]}
      placement="bottomRight"
    >
      <Tooltip
        placement={"left"}
        title={activeSortLabel ? `Sort by ${activeSortLabel}` : ""}
      >
        <Button
          type={activeSortLabel ? "primary" : "default"}
          icon={
            currentSort.order === "desc" ? (
              <SortDescendingOutlined />
            ) : (
              <SortAscendingOutlined />
            )
          }
        >
          Sort
        </Button>
      </Tooltip>
    </Dropdown>
  )
}
